import * as React from 'react';
import { Payment, PaymentStatus } from '../../../models';
import { formatEuros } from '../../../shared/string';
import { useTranslations } from '../../../store/translations/hooks';
import Delete from '@material-ui/icons/Delete';
import AccountBalance from '@mui/icons-material/AccountBalance';
import CreditCard from '@mui/icons-material/CreditCard';
import Paid from '@mui/icons-material/Paid';
import ShoppingCartCheckout from '@mui/icons-material/ShoppingCartCheckout';
import { TK } from '../../../store/translations/translationKeys';
import { FC } from 'react';
import AppIconButton from '../../AppIconButton';
import moment from 'moment';

export interface PaymentsListProps {
    values: Payment[],
    canDelete: boolean,
    onDelete: (payment: Payment, idx: number) => void,
    missingToPay?: number,
}

const methodIconSrc: Partial<Record<string, string>> = {
    'auto-mb':           '/payment-methods/MULTIBANCO.svg',
    'auto-mbw':          '/payment-methods/MBWAY.svg',
    'auto-apple-pay':    '/payment-methods/APPLE-PAY.svg',
    'auto-google-pay':   '/payment-methods/GOOGLE-PAY.svg',
    'auto-samsung-wallet': '/payment-methods/SAMSUNG-PAY.svg',
    'auto-credit-card':  '/payment-methods/CREDIT-CARD.svg',
    'auto-digital-iban': '/payment-methods/DIGITAL-IBAN.svg',
};

const isCash = (t: string) => ['money', 'dinheiro', 'numerário', 'numerario'].includes(t.toLowerCase());
const isBank = (t: string) => ['bank', 'transf', 'transferência', 'transferencia'].some(k => t.toLowerCase().includes(k));
const isMBWay = (t: string) => ['mbway', 'mb way'].includes(t.toLowerCase());
const isCard = (t: string) => ['tpa', 'card', 'cartão', 'cartao', 'multibanco'].some(k => t.toLowerCase().includes(k));

const iconStyle: React.CSSProperties = {display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)'};
const imgStyle: React.CSSProperties = {maxHeight: '25px', height: '-webkit-fill-available', alignSelf: 'flex-start'};

const WithLabel = ({icon, label, style}: {icon: React.ReactNode, label: string, style?: React.CSSProperties}) => (
    <span style={{...iconStyle, ...style}}>{icon}<span>{label}</span></span>
);

export const PaymentLabel: FC<{paymentType: string, style?: React.CSSProperties}> = ({paymentType, style}) => {
    const type = paymentType ?? '';
    const src = methodIconSrc[type];
    if (src) return <img style={{...imgStyle, ...style}} src={src}/>;
    if (type === 'checkout') return <WithLabel style={style} icon={<ShoppingCartCheckout fontSize="small"/>} label={type}/>;
    if (isMBWay(type)) return <img style={{...imgStyle, ...style}} src='/payment-methods/MBWAY.svg'/>
    if (isCash(type)) return <WithLabel style={style} icon={<Paid fontSize="small"/>} label={type}/>;
    if (isBank(type)) return <WithLabel style={style} icon={<AccountBalance fontSize="small"/>} label={type}/>;
    if (isCard(type)) return <WithLabel style={style} icon={<CreditCard fontSize="small"/>} label={type}/>;
    return <>{type}</>;
}

export const PaymentsList = (props: PaymentsListProps) => {
    const {
        values,
        canDelete,
        onDelete,
        missingToPay,
    } = props;

    const t = useTranslations();
    const [showAll, setShowAll] = React.useState(false);

    const hasSettled = values.some(p => p.status === PaymentStatus.Success || p.status === PaymentStatus.Manual);
    const isPaid = hasSettled && (missingToPay ?? 1) <= 0;

    const displayValues = React.useMemo(() => {
        const indexed = values.map((payment, idx) => ({payment, idx}));

        if (showAll) return indexed;

        if (isPaid) {
            return indexed.filter(({payment: p}) =>
                p.status === PaymentStatus.Success || p.status === PaymentStatus.Manual
            );
        }

        if (!hasSettled) {
            const nonSettled = indexed.filter(({payment: p}) =>
                p.status === PaymentStatus.InProgress || p.status === PaymentStatus.Failed
            );
            if (nonSettled.length <= 1) return indexed;
            const latest = nonSettled.reduce((prev, curr) =>
                (curr.payment.date ?? '') > (prev.payment.date ?? '') ? curr : prev
            );
            return indexed.filter(({idx: i}) => i === latest.idx);
        }

        return indexed;
    }, [values, showAll, isPaid, hasSettled]);

    const hasHidden = displayValues.length < values.length;

    const autoPaymentColor = (status: PaymentStatus): string => {
        switch (status) {
            case PaymentStatus.Failed: return 'var(--danger-color)';
            case PaymentStatus.Success: return 'var(--success-color)';
            default: return 'var(--text-muted)';
        }
    }

    const autoPaymentStatusText = (payment: Payment): string => {
        switch (payment.status) {
            case PaymentStatus.Failed: return `(${t(TK.failed)})`;
            case PaymentStatus.InProgress: return payment.notes ? `(${t(TK.inProgress)}, ${payment.notes})` :`(${t(TK.inProgress)})`;
            case PaymentStatus.Manual: return `(${t(TK.manual)})`;
            case PaymentStatus.Success: return `(${t(TK.paid)})`;
            default: return `(${t(TK.unknown)})`;
        }
    }
    
    React.useEffect(() => { setShowAll(false); }, [values]);

    const paidPayments = values.filter(p => p.status === PaymentStatus.Success || p.status === PaymentStatus.Manual);
    const showTotal = paidPayments.length > 1;
    const total = paidPayments.reduce((sum, p) => sum + (p.value ?? 0), 0);

    return (
        <div>
            {displayValues.map(({payment, idx}) => (
                <div key={idx} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--spacer-XS)', borderBottom: 'var(--border-thin) solid var(--appLigthGrey)'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 'var(--spacer-S)'}}>
                        {canDelete && payment.status === PaymentStatus.Manual && (
                            <AppIconButton onClick={() => onDelete(payment, idx)}>
                                <Delete fontSize="small" color="error" />
                            </AppIconButton>
                        )}
                        <span style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XXS)'}}>
                            <PaymentLabel paymentType={payment.type}/>
                            <span style={{color: 'var(--text-muted)', fontSize: '0.75rem'}}>
                                {payment.date ? moment.utc(payment.date).local().format('YYYY-MM-DD HH:mm') : '—'}
                            </span>
                        </span>
                    </div>
                    <span style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 'var(--spacer-XXS)'}}>
                        <span style={{
                            opacity: (payment.status === PaymentStatus.Success || payment.status === PaymentStatus.Manual) ? 1 : 0.7,
                            fontWeight: (payment.status === PaymentStatus.Success || payment.status === PaymentStatus.Manual) ? 'bold' : 'unset',
                        }}>
                            {formatEuros(payment.value)}
                        </span>
                        {payment.status && payment.status !== PaymentStatus.Manual && (
                            <span style={{fontStyle: 'italic', fontSize: '0.75rem', color: autoPaymentColor(payment.status)}}>
                                {autoPaymentStatusText(payment)}
                            </span>
                        )}
                    </span>
                </div>
            ))}
            {hasHidden && !showAll && (
                <div style={{padding: 'var(--spacer-XS)'}}>
                    <button
                        type="button"
                        onClick={() => setShowAll(true)}
                        style={{background: 'none', border: 'none', padding: 0, color: 'var(--primary-color)', cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline'}}
                    >
                        {t(TK.showAllPaymentAttempts)}
                    </button>
                </div>
            )}
            {showTotal && (
                <div style={{display: 'flex', justifyContent: 'space-between', padding: 'var(--spacer-XS)', borderTop: 'var(--border-medium) solid var(--appLigthGrey)', marginTop: 'var(--spacer-XS)', fontWeight: 'bold'}}>
                    <span>{t(TK.totalPaid)}</span>
                    <span>{formatEuros(total)}</span>
                </div>
            )}
            {missingToPay != null && missingToPay > 0 && (
                <div style={{display: 'flex', justifyContent: 'space-between', padding: 'var(--spacer-XS)', fontWeight: 'bold', color: 'var(--danger-color)'}}>
                    <span></span>
                    <span>{t(TK.missingToPay)} {formatEuros(missingToPay)}</span>
                </div>
            )}
            {missingToPay != null && missingToPay < 0 && (
                <div style={{display: 'flex', justifyContent: 'space-between', padding: 'var(--spacer-XS)', fontWeight: 'bold', color: 'var(--warning-color)'}}>
                    <span></span>
                    <span>{t(TK.overpaidAmount)} {formatEuros(-missingToPay)}</span>
                </div>
            )}
        </div>
    );
}
